import { BaseListViewStore, BooleanToggleCellData } from 'core/stores';
import { action, runInAction } from 'mobx';
import { ApplicationStore } from 'platform/application/stores';
import { observable, makeObservable } from 'mobx';
import { notificationError, isSuperAdminOrganization } from 'core/utils';
import { SellerOrganizationInfoStore } from 'modules/seller/stores';
import { sourceOfChange } from 'common/constants';
import _ from 'lodash';
import { SellerEdit3PLCredentials, SellerWarehouse } from '../pages';

class SellerListViewStore extends BaseListViewStore {
	@observable applicationSort = [{ field: 'applicationTitle', dir: 'asc' }];
	application;

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ApplicationStore(rootStore),
			findFunction: 'findSellerOrganization',
			queryParams: {
				embed: ['sellerOrganizationInfo'],
			},
			tableData: {
				columns: [
					{
						key: 'applicationTitle',
						title: 'SELLERVIEW.APPLICATIONTITLE',
						selected: true,
						width: 200,
						sortable: true,
					},
					{
						key: 'identifier',
						title: 'SELLERVIEW.IDENTIFIER',
						selected: true,
						width: 200,
						sortable: true,
					},
					{
						key: 'sellerOrganizationInfo.printPackingSlip',
						title: 'SELLERVIEW.PRINTPACKINGSLIP',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (seller, field) => this.onToggle(seller, field, sourceOfChange.printPackingSlipToggleSellerList),
						isClickable: true,
					},
					{
						key: 'sellerOrganizationInfo.partiallyFulfillMixedOrders',
						title: 'SELLERVIEW.SPLITORDERSWITHNONACTIVESKU',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (seller, field) =>
							this.onToggle(seller, field, sourceOfChange.partiallyFulfillMixedOrdersToggleSellerList),
						isClickable: true,
					},
					{
						key: 'sellerOrganizationInfo.isAutomatedTransferEnabled',
						title: 'SELLERVIEW.AUTOMATICTRANSFER',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (seller, field) => this.onToggle(seller, field, sourceOfChange.enableAutomatedTransferSellerList),
						isClickable: true,
					},
				],
				sortObject: [],
				actions: {
					onEdit: application => this.routes.edit(application.id),
					onChangeOrganizationActive: item => this.openConfirmationModal(this.getActivationConfirmModalProps(item)),
					onEditSeller3PLCredentials: async application => this.openEditSeller3PLCredentialsModal(application),
					onEditSellerWarehouses: async application => this.openEditSellerWarehousesModal(application),
				},
			},
			routes: {
				create: () => this.rootStore.routerStore.goTo('master.app.main.seller.create'),
				edit: id =>
					this.rootStore.routerStore.goTo('master.app.main.seller.edit', {
						id,
					}),
			},
			permissions: {
				onEdit: () => this.authStore.hasPermission(''),
				onChangeOrganizationActive: () => this.authStore.hasPermission(''),
				onEditSeller3PLCredentials: () => this.authStore.hasPermission(''),
				onEditSellerWarehouses: () => this.authStore.hasPermission(''),
			},
		});
		makeObservable(this);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		if (isSuperAdminOrganization(this.rootStore)) {
			await this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.sellerOrganizationInfoStore = new SellerOrganizationInfoStore(this.rootStore);
		await this.queryUtility.initialize(this.findSellerOrganization);
		this.setButtons();
		this.setSearchVisible();
	};

	@action.bound async findSellerOrganization() {
		this.suspend();
		runInAction(() => {
			//Default sorting
			this.queryUtility.filter.sellerOnly = true;
		});

		await this.listStore.find(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.resume();
	}

	@action.bound setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-plus spc--right--sml',
					onClick: this.routes.create,
					authorization: 'platformSection.create',
					text: 'SELLERVIEW.NEWSELLER',
				},
			],
		});
	}

	@action.bound async changeOrganizationActive(application) {
		this.suspend();
		try {
			await this.listStore.changeStatus(application.id, !application.isActive);
			this.rootStore.notificationStore.success([
				!application.isActive ? 'ORGANIZATION.LIST.REACTIVATESUCCESS' : 'ORGANIZATION.LIST.DEACTIVATESUCCESS',
				{ name: application.identifier },
			]);
			await this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	openEditSeller3PLCredentialsModal = item => {
		this.openModal({
			data: { parentView: this, selectedItem: item },
			component: SellerEdit3PLCredentials,
			className: 'ReactModal--lrg',
		});
	};

	openEditSellerWarehousesModal = item => {
		this.openModal({
			data: { ...this, selectedItem: item },
			component: SellerWarehouse,
			className: 'ReactModal--sml-med',
		});
	};

	@action.bound
	onToggle = async (seller, field, sourceOfChange) => {
		this.suspend();
		try {
			const fields = field.split('.');
			const data = _.get(seller, fields[0]);
			data[fields[1]] = !data[fields[1]];
			await this.sellerOrganizationInfoStore.updateSellerOrganizationInfo(data, sourceOfChange);
			this.rootStore.notificationStore.success(['LOOKUPUPDATE.SELLER', { seller: seller.applicationTitle }]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	getActivationConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.changeOrganizationActive(this.rootStore.viewStore.selectedConfirmModal.data),
			message: [
				!item.isActive ? 'ORGANIZATION.LIST.REACTIVATECONFIRM' : 'ORGANIZATION.LIST.DEACTIVATECONFIRM',
				{ name: item.identifier },
			],
			data: item,
		};
	};
}

export default SellerListViewStore;



// WEBPACK FOOTER //
// ./src/modules/seller/stores/SellerListViewStore.js